import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  ProgrammeIcon,
  ProgrammeLogo,
} from '../components/programmes/ProgrammeUI';
import { getProgramme } from '../data/programmeJourneys';
import {
  CREDENTIAL_STATUS_CONTENT,
  ONBOARDING_STATUS_CONTENT,
  PROGRAMME_SYSTEM_MESSAGES,
  getApplicationStatusContent,
  getOnboardingStatus,
  normalizeProgrammeStatus,
} from '../data/programmeMessages';
import { getProgrammePortalData } from '../data/programmePortalData';
import {
  clearProgrammeSession,
  getOnboardingTasks,
  getLiveProgrammeCohort,
  getLiveProgrammeCredential,
  getLiveProgrammePortal,
  getLiveProgrammeResources,
  getLiveProgrammeUpdates,
  getPortalProfile,
  getProgrammeApplication,
  getProgrammeSession,
  saveOnboardingTask,
  updateLiveOnboardingTask,
} from '../services/programmeJourneyService';
import '../styles/pages/ProgrammeJourneys.css';

const getStatusContent = (status) => {
  const normalizedStatus = normalizeProgrammeStatus(status);
  const statusContent = getApplicationStatusContent(normalizedStatus);
  return {
    ...statusContent,
    icon:
      normalizedStatus === 'approved' || normalizedStatus === 'selected'
        ? 'check'
        : normalizedStatus === 'more_information_required'
          ? 'info'
          : 'document',
  };
};

const PortalNav = ({
  activeView,
  items,
  onChange,
  profile,
  programme,
}) => (
  <aside className="pj-portal-sidebar">
    <ProgrammeLogo compact />
    <nav aria-label={`${programme.title} portal navigation`}>
      {items.map((item) => (
        <button
          aria-current={activeView === item.id ? 'page' : undefined}
          className={activeView === item.id ? 'is-active' : ''}
          key={item.id}
          onClick={() => onChange(item.id)}
          type="button"
        >
          <ProgrammeIcon name={item.icon} size={20} />
          {item.label}
        </button>
      ))}
    </nav>
    <div className="pj-portal-sidebar__footer">
      <div className="pj-portal-user">
        <span className="pj-portal-user__avatar">
          {profile.profileImage ? (
            <img alt="" src={profile.profileImage} />
          ) : (
            profile.initials
          )}
        </span>
        <div>
          <strong>{profile.fullName}</strong>
          <small>{profile.email}</small>
        </div>
      </div>
      <Link
        onClick={() => clearProgrammeSession(programme.key)}
        to={`/${programme.slug}/login`}
      >
        <ProgrammeIcon name="back" size={17} />
        Sign out
      </Link>
    </div>
  </aside>
);

const StatusStrip = ({ onboardingComplete, programme, status }) => {
  const content = getStatusContent(status);
  const normalizedStatus = normalizeProgrammeStatus(status);
  const onboardingAvailable =
    (programme.key === 'zone' && normalizedStatus === 'approved') ||
    (programme.key === 'fellowship' && normalizedStatus === 'selected');
  const message =
    onboardingAvailable && onboardingComplete && programme.key === 'zone'
      ? 'Your research onboarding is complete and your credential is active.'
      : onboardingAvailable && onboardingComplete && programme.key === 'fellowship'
        ? 'Your onboarding is complete. Programme updates and your cohort space are ready.'
        : content.message;

  return (
    <section className="pj-status-strip" aria-label="Application status">
      <span className="pj-status-strip__icon" aria-hidden="true">
        <ProgrammeIcon name={content.icon} size={23} />
      </span>
      <div>
        <small>Application status</small>
        <strong>{content.label}</strong>
      </div>
      <p>{message}</p>
    </section>
  );
};

const PhaseTrack = ({ onboardingComplete, programme, status }) => {
  const normalizedStatus = normalizeProgrammeStatus(status);
  const approvedStatus = programme.key === 'zone' ? 'approved' : 'selected';
  const approved = normalizedStatus === approvedStatus;
  const phases = approved
    ? programme.key === 'zone'
      ? ['Approved', 'Onboarding', 'Experiment active']
      : ['Selected', 'Onboarding', 'Cohort begins', 'Fellowship active']
    : programme.key === 'zone'
      ? ['Application review', 'Approved', 'Onboarding', 'Experiment active']
      : ['Application review', 'Selected', 'Onboarding', 'Cohort begins'];
  const activeIndex = approved ? (onboardingComplete ? 2 : 1) : 0;

  return (
    <section className="pj-portal-panel pj-phase-panel">
      <h2>{programme.key === 'zone' ? 'Current phase' : 'Programme journey'}</h2>
      <ol className="pj-phase-track">
        {phases.map((phase, index) => (
          <li
            className={
              index < activeIndex
                ? 'is-complete'
                : index === activeIndex
                  ? 'is-active'
                  : ''
            }
            key={phase}
          >
            <span>
              {index < activeIndex ? (
                <ProgrammeIcon name="check" size={17} />
              ) : (
                index + 1
              )}
            </span>
            <strong>{phase}</strong>
            <small>
              {index < activeIndex
                ? 'Complete'
                : index === activeIndex
                  ? approved
                    ? 'In progress'
                    : 'Awaiting review'
                  : 'Pending'}
            </small>
          </li>
        ))}
      </ol>
    </section>
  );
};

const UpdatesPanel = ({
  compact = false,
  onOpen,
  onViewAll,
  programme,
  updates,
}) => (
  <section className="pj-portal-panel pj-updates-panel">
    <div className="pj-panel-heading">
      <h2>{programme.key === 'zone' ? 'Updates' : 'Programme updates'}</h2>
      {compact ? (
        <button onClick={onViewAll} type="button">
          View all
        </button>
      ) : null}
    </div>
    {updates.length > 0 ? (
      <ul>
        {updates.map((update) => (
          <li key={update.id}>
            <button
              aria-label={`Read ${update.title}`}
              className="pj-list-action pj-update-action"
              onClick={() => onOpen(update)}
              type="button"
            >
              <span className="pj-update-dot" aria-hidden="true" />
              <time>{update.date}</time>
              <span className="pj-list-action__copy">
                <strong>{update.title}</strong>
                <span>{update.summary}</span>
              </span>
              <ProgrammeIcon name="arrow" size={17} />
            </button>
          </li>
        ))}
      </ul>
    ) : (
      <p className="pj-panel-empty">{PROGRAMME_SYSTEM_MESSAGES.emptyUpdates}</p>
    )}
  </section>
);

const ResourcesPanel = ({
  compact = false,
  onOpen,
  onViewAll,
  programme,
  resources,
}) => (
  <section className="pj-portal-panel pj-resources-panel">
    <div className="pj-panel-heading">
      <h2>{programme.key === 'zone' ? 'Documents' : 'Resource library'}</h2>
      {compact ? (
        <button onClick={onViewAll} type="button">
          View all
        </button>
      ) : null}
    </div>
    {resources.length > 0 ? (
      <ul>
        {resources.map((resource) => (
          <li key={resource.id}>
            <button
              aria-label={`Open ${resource.title}`}
              className="pj-list-action pj-resource-action"
              onClick={() => onOpen(resource)}
              type="button"
            >
              <span className="pj-resource-icon" aria-hidden="true">
                <ProgrammeIcon name="document" size={18} />
              </span>
              <span className="pj-list-action__copy">
                <strong>{resource.title}</strong>
                <span>{resource.type}</span>
              </span>
              <ProgrammeIcon name="arrow" size={17} />
            </button>
          </li>
        ))}
      </ul>
    ) : (
      <p className="pj-panel-empty">
        {PROGRAMME_SYSTEM_MESSAGES.emptyResources}
      </p>
    )}
  </section>
);

const formatCredentialDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(date);
};

const CredentialPanel = ({
  credential,
  credentialState,
  onOpen,
  profile,
}) => {
  const active = credentialState === 'active';
  const content =
    CREDENTIAL_STATUS_CONTENT[credentialState] ||
    CREDENTIAL_STATUS_CONTENT.not_issued;

  return (
    <section className="pj-portal-panel pj-credential">
      <div className="pj-panel-heading">
        <div>
          <span aria-hidden="true">
            <ProgrammeIcon name="shield" size={23} />
          </span>
          <h2>Supernova Lab Research Credential</h2>
        </div>
        <span className="pj-status-pill">{content.label}</span>
      </div>
      {active ? (
        <>
          <div className="pj-credential__body">
            <dl>
              <div>
                <dt>Credential ID</dt>
                <dd>{credential?.credential_code || 'Pending issue'}</dd>
              </div>
              <div>
                <dt>Holder</dt>
                <dd>{profile.fullName}</dd>
              </div>
              <div>
                <dt>Programme</dt>
                <dd>Experimentation Zone</dd>
              </div>
              <div>
                <dt>Issue date</dt>
                <dd>{formatCredentialDate(credential?.issued_at)}</dd>
              </div>
              <div>
                <dt>Valid until</dt>
                <dd>{formatCredentialDate(credential?.expires_at)}</dd>
              </div>
            </dl>
            <button
              aria-label="View research credential details"
              className="pj-credential-mark"
              onClick={onOpen}
              type="button"
            >
              <span className="pj-credential-pattern" aria-hidden="true">
                {Array.from({ length: 25 }, (_, index) => (
                  <span key={index} />
                ))}
              </span>
              <small>View credential</small>
            </button>
          </div>
        </>
      ) : (
        <div className="pj-empty-panel">
          <span aria-hidden="true">
            <ProgrammeIcon name="lock" size={25} />
          </span>
          <div>
            <h3>{content.title}</h3>
            <p>{content.message}</p>
          </div>
        </div>
      )}
      <p className="pj-credential__scope">
        This programme credential confirms participation in the Supernova Lab
        Experimentation Zone. It does not replace any legal, regulatory,
        ethical, professional or sector-specific approval required for the
        experiment.
      </p>
    </section>
  );
};

const getNextActionContent = ({
  approved,
  onboardingComplete,
  programme,
  status,
}) => {
  const normalizedStatus = normalizeProgrammeStatus(status);

  if (onboardingComplete) {
    return programme.key === 'zone'
      ? {
          title: 'Review your research credential',
          message:
            'Your credential details and verification information are available in the portal.',
          label: 'View research credential',
        }
      : {
          title: 'Explore your cohort',
          message:
            'Meet the fellows building alongside you and learn about their areas of focus.',
          label: 'Meet your cohort',
        };
  }

  if (approved) {
    return programme.key === 'zone'
      ? {
          title: 'Complete research onboarding',
          message:
            'Complete each onboarding task before programme access is activated.',
          label: 'Start onboarding',
        }
      : {
          title: 'Complete your fellowship onboarding',
          message:
            'Complete the required information before the programme begins.',
          label: 'Start onboarding',
        };
  }

  if (normalizedStatus === 'more_information_required') {
    return {
      title: 'More information is required',
      message:
        'Review the programme team’s request and provide the additional details.',
      label: 'Provide requested information',
    };
  }

  if (normalizedStatus === 'shortlisted') {
    return {
      title: 'Review the next stage',
      message:
        'Your application has moved forward. Review the latest programme update and any response deadline.',
      label: 'Review next-stage instructions',
    };
  }

  if (
    normalizedStatus === 'not_selected' ||
    normalizedStatus === 'withdrawn'
  ) {
    return {
      title: getApplicationStatusContent(normalizedStatus).label,
      message: getApplicationStatusContent(normalizedStatus).message,
      label: 'View submitted application',
    };
  }

  return {
    title: 'Your application is awaiting review',
    message:
      'No action is needed right now. We’ll post any questions, decisions and next steps here.',
    label: 'View submitted application',
  };
};

const NextActionPanel = ({
  approved,
  onboardingComplete,
  onAction,
  programme,
  status,
}) => {
  const content = getNextActionContent({
    approved,
    onboardingComplete,
    programme,
    status,
  });

  return (
    <section className="pj-portal-panel pj-next-action">
      <span aria-hidden="true">
        <ProgrammeIcon
          name={
            approved
              ? programme.key === 'zone'
                ? 'shield'
                : 'users'
              : 'document'
          }
          size={25}
        />
      </span>
      <small>Your next action</small>
      <h2>{content.title}</h2>
      <p>{content.message}</p>
      <button
        className={`pj-button ${approved ? 'pj-button--primary' : 'pj-button--secondary'}`}
        onClick={onAction}
        type="button"
      >
        {content.label}
        <ProgrammeIcon name="arrow" size={18} />
      </button>
    </section>
  );
};

const OnboardingPanel = ({ compact = false, onOpen, tasks }) => {
  const completeCount = tasks.filter(
    (task) => getOnboardingStatus(task) === 'complete'
  ).length;

  return (
    <section className="pj-portal-panel pj-onboarding-panel">
      <div className="pj-panel-heading">
        <h2>Onboarding checklist</h2>
        <span>
          {completeCount} of {tasks.length}
        </span>
      </div>
      <div
        aria-label={`${completeCount} of ${tasks.length} onboarding tasks complete`}
        aria-valuemax={tasks.length}
        aria-valuemin="0"
        aria-valuenow={completeCount}
        className="pj-onboarding-progress"
        role="progressbar"
      >
        <span style={{ width: `${(completeCount / tasks.length) * 100}%` }} />
      </div>
      <ul>
        {(compact ? tasks.slice(0, 4) : tasks).map((task) => (
          <li key={task.id}>
            {(() => {
              const taskStatus = getOnboardingStatus(task);
              const statusContent =
                ONBOARDING_STATUS_CONTENT[taskStatus] ||
                ONBOARDING_STATUS_CONTENT.not_started;
              const complete = taskStatus === 'complete';
              const locked = taskStatus === 'locked';

              return (
                <button
                  aria-label={`${complete ? 'Review' : 'Open'} ${task.title}`}
                  className="pj-list-action pj-task-action"
                  disabled={locked}
                  onClick={() => onOpen(task)}
                  type="button"
                >
                  <span
                    className={`pj-task-state ${complete ? 'is-complete' : ''}`}
                    aria-hidden="true"
                  >
                    {complete ? (
                      <ProgrammeIcon name="check" size={14} />
                    ) : null}
                  </span>
                  <span className="pj-list-action__copy">
                    <strong>{task.title}</strong>
                    <span>{statusContent.label}</span>
                  </span>
                  <ProgrammeIcon name="arrow" size={16} />
                </button>
              );
            })()}
          </li>
        ))}
      </ul>
    </section>
  );
};

const CohortPanel = ({ compact = false, members, onOpen, onViewAll }) => (
  <section className="pj-portal-panel pj-cohort-panel">
    <div className="pj-panel-heading">
      <h2>Meet your cohort</h2>
      {compact ? (
        <button onClick={onViewAll} type="button">
          View all
        </button>
      ) : null}
    </div>
    {compact ? (
      <>
        <div className="pj-cohort-people" aria-label="Cohort members">
          {members.map((member, index) => (
            <button
              aria-label={`View ${member.name}`}
              key={member.id}
              onClick={() => onOpen(member)}
              style={{ '--avatar-index': index }}
              type="button"
            >
              {member.profileImage ? (
                <img alt="" src={member.profileImage} />
              ) : (
                member.initials
              )}
            </button>
          ))}
        </div>
        <p>Open a profile to learn what each fellow is exploring.</p>
      </>
    ) : (
      <div className="pj-cohort-grid">
        {members.map((member, index) => (
          <button
            key={member.id}
            onClick={() => onOpen(member)}
            type="button"
          >
            <span style={{ '--avatar-index': index }}>
              {member.profileImage ? (
                <img alt="" src={member.profileImage} />
              ) : (
                member.initials
              )}
            </span>
            <span>
              <strong>{member.name}</strong>
              <small>{member.role}</small>
            </span>
            <ProgrammeIcon name="arrow" size={17} />
          </button>
        ))}
      </div>
    )}
  </section>
);

const ApplicationSummary = ({ application, profile, programme }) => {
  const formData = application?.data;
  const statusContent = getApplicationStatusContent(
    profile.applicationStatus
  );

  return (
    <section className="pj-portal-panel pj-application-record">
      <div className="pj-application-record__header">
        <span aria-hidden="true">
          <ProgrammeIcon name="document" size={28} />
        </span>
        <div>
          <small>Application reference</small>
          <h2>{profile.applicationReference}</h2>
        </div>
        <span className="pj-status-pill">{statusContent.label}</span>
      </div>

      {formData ? (
        <div className="pj-application-sections">
          {programme.applicationSteps.map((applicationStep) => (
            <section key={applicationStep.title}>
              <h3>{applicationStep.title}</h3>
              <dl>
                {applicationStep.fields.map((field) => (
                  <div key={field.name}>
                    <dt>{field.label}</dt>
                    <dd>{formData[field.name] || 'Not provided'}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      ) : (
        <div className="pj-empty-panel">
          <span aria-hidden="true">
            <ProgrammeIcon name="info" size={25} />
          </span>
          <div>
            <h3>Application details</h3>
            <p>
              Submitted answers and programme review information will appear
              here.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

const PortalDialog = ({ children, eyebrow, onClose, title }) => {
  const closeButtonRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;

      const focusableElements = dialogRef.current?.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements?.length) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('pj-dialog-open');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('pj-dialog-open');
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="pj-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <section
        aria-labelledby="pj-dialog-title"
        aria-modal="true"
        className="pj-dialog"
        ref={dialogRef}
        role="dialog"
      >
        <header className="pj-dialog__header">
          <div>
            {eyebrow ? <p>{eyebrow}</p> : null}
            <h2 id="pj-dialog-title">{title}</h2>
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            ref={closeButtonRef}
            type="button"
          >
            <ProgrammeIcon name="close" size={22} />
          </button>
        </header>
        <div className="pj-dialog__body">{children}</div>
      </section>
    </div>
  );
};

const TaskForm = ({ onSave, task }) => {
  const [values, setValues] = useState(task.values);
  const [errors, setErrors] = useState({});
  const taskStatus = getOnboardingStatus(task);
  const statusContent =
    ONBOARDING_STATUS_CONTENT[taskStatus] ||
    ONBOARDING_STATUS_CONTENT.not_started;

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = {};

    task.fields.forEach((field) => {
      const value = values[field.name];
      if (
        field.required &&
        (field.type === 'checkbox' ? !value : !String(value || '').trim())
      ) {
        nextErrors[field.name] = 'Complete this field to continue.';
      }
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) onSave(values);
  };

  return (
    <form className="pj-task-form" noValidate onSubmit={submit}>
      <p>{task.description}</p>
      <p className="pj-task-status-message">{statusContent.message}</p>
      <div className="pj-task-form__fields">
        {task.fields.map((field) => (
          <div className="pj-field pj-field--wide" key={field.name}>
            {field.type === 'checkbox' ? (
              <label className="pj-task-checkbox">
                <input
                  checked={Boolean(values[field.name])}
                  onChange={(event) =>
                    setValues((current) => ({
                      ...current,
                      [field.name]: event.target.checked,
                    }))
                  }
                  type="checkbox"
                />
                <span>{field.label}</span>
              </label>
            ) : (
              <>
                <label htmlFor={`task-${task.id}-${field.name}`}>
                  {field.label}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    id={`task-${task.id}-${field.name}`}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [field.name]: event.target.value,
                      }))
                    }
                    placeholder={field.placeholder}
                    rows="5"
                    value={values[field.name] || ''}
                  />
                ) : field.type === 'select' ? (
                  <select
                    id={`task-${task.id}-${field.name}`}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [field.name]: event.target.value,
                      }))
                    }
                    value={values[field.name] || ''}
                  >
                    <option value="">Select an option</option>
                    {field.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={`task-${task.id}-${field.name}`}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        [field.name]: event.target.value,
                      }))
                    }
                    placeholder={field.placeholder}
                    type={field.type}
                    value={values[field.name] || ''}
                  />
                )}
              </>
            )}
            {errors[field.name] ? (
              <span className="pj-field-error" role="alert">
                {errors[field.name]}
              </span>
            ) : null}
          </div>
        ))}
      </div>
      <button className="pj-button pj-button--primary" type="submit">
        {taskStatus === 'complete' ? 'Save changes' : 'Complete task'}
        <ProgrammeIcon name="check" size={18} />
      </button>
    </form>
  );
};

const ProgrammePortalPage = ({ kind }) => {
  const programme = getProgramme(kind);
  const fallbackPortalData = getProgrammePortalData(programme.key);
  const session = getProgrammeSession(programme.key);
  const fallbackProfile = getPortalProfile(programme.key);
  const fallbackApplication = getProgrammeApplication(programme.key);
  const [livePortal, setLivePortal] = useState(null);
  const [liveUpdates, setLiveUpdates] = useState(null);
  const [liveResources, setLiveResources] = useState(null);
  const [liveCredential, setLiveCredential] = useState(null);
  const [liveCohort, setLiveCohort] = useState(null);
  const profile = livePortal?.profile || fallbackProfile;
  const application = livePortal?.application || fallbackApplication;
  const portalData = useMemo(
    () => ({
      ...fallbackPortalData,
      updates: liveUpdates || fallbackPortalData.updates,
      resources: liveResources || fallbackPortalData.resources,
      cohort: liveCohort?.members || fallbackPortalData.cohort,
    }),
    [fallbackPortalData, liveCohort, liveResources, liveUpdates]
  );
  const normalizedStatus = normalizeProgrammeStatus(
    profile.applicationStatus
  );
  const approvedStatus = programme.key === 'zone' ? 'approved' : 'selected';
  const approved = normalizedStatus === approvedStatus;
  const navItems = useMemo(
    () => {
      const items =
        programme.key === 'zone'
          ? [
              { id: 'overview', label: 'Overview', icon: 'home' },
              { id: 'application', label: 'Application', icon: 'document' },
              { id: 'updates', label: 'Updates', icon: 'signal' },
            ]
          : [
              { id: 'overview', label: 'Home', icon: 'home' },
              {
                id: 'application',
                label: 'My application',
                icon: 'document',
              },
              {
                id: 'updates',
                label: 'Programme updates',
                icon: 'signal',
              },
            ];

      if (!approved) return items;

      return programme.key === 'zone'
        ? [
            ...items.slice(0, 2),
            { id: 'onboarding', label: 'Onboarding', icon: 'check' },
            {
              id: 'credential',
              label: 'Research credential',
              icon: 'shield',
            },
            ...items.slice(2),
            { id: 'resources', label: 'Documents', icon: 'download' },
          ]
        : [
            ...items.slice(0, 2),
            { id: 'cohort', label: 'Cohort', icon: 'users' },
            ...items.slice(2),
            { id: 'onboarding', label: 'Onboarding', icon: 'check' },
            { id: 'resources', label: 'Resources', icon: 'download' },
          ];
    },
    [approved, programme.key]
  );
  const [activeView, setActiveView] = useState('overview');
  const [tasks, setTasks] = useState(() =>
    getOnboardingTasks(programme.key)
  );
  const [dialog, setDialog] = useState(null);
  const [copied, setCopied] = useState(false);
  const [portalMessage, setPortalMessage] = useState('');

  useEffect(() => {
    if (!session?.token) return undefined;
    const controller = new AbortController();

    const loadPortal = async () => {
      try {
        const [portalResponse, updatesResponse, resourcesResponse, programmeResponse] =
          await Promise.all([
            getLiveProgrammePortal(programme.key, {
              signal: controller.signal,
            }),
            getLiveProgrammeUpdates(programme.key, {
              signal: controller.signal,
            }),
            getLiveProgrammeResources(programme.key, {
              signal: controller.signal,
            }),
            programme.key === 'zone'
              ? getLiveProgrammeCredential({ signal: controller.signal })
              : getLiveProgrammeCohort({ signal: controller.signal }),
          ]);

        setLivePortal(portalResponse);
        setTasks(portalResponse.onboardingTasks || []);
        setLiveUpdates(updatesResponse.data || []);
        setLiveResources(resourcesResponse.data || []);
        if (programme.key === 'zone') {
          setLiveCredential(programmeResponse.data);
        } else {
          setLiveCohort(programmeResponse.data);
        }
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setPortalMessage(
            'The latest portal data could not be loaded. Refresh to try again.'
          );
        }
      }
    };

    loadPortal();
    return () => controller.abort();
  }, [programme.key, session?.token]);

  const credentialState =
    programme.key !== 'zone'
      ? 'not_issued'
      : !approved
        ? 'not_issued'
        : liveCredential?.status === 'issued' &&
            tasks.every(
              (task) => getOnboardingStatus(task) === 'complete'
            )
          ? 'active'
        : tasks.every(
              (task) => getOnboardingStatus(task) === 'complete'
            )
          ? 'pending'
          : 'pending';
  const onboardingComplete =
    approved &&
    tasks.every((task) => getOnboardingStatus(task) === 'complete');
  const visibleUpdates = useMemo(
    () =>
      profile.isExample || approved
        ? portalData.updates
        : [
            {
              id: `${programme.key}-application-submitted`,
              date: 'Today',
              title: 'Application received.',
              summary: `Reference ${profile.applicationReference}`,
              body:
                'Your application is in the review queue. The programme team will publish any questions, decisions and next steps here.',
              actionLabel: 'View application',
              actionView: 'application',
            },
          ],
    [
      approved,
      portalData.updates,
      profile.applicationReference,
      profile.isExample,
      programme.key,
    ]
  );

  const openDialog = (type, item) => setDialog({ type, item });
  const closeDialog = () => {
    setDialog(null);
    setCopied(false);
  };

  const saveTask = async (task, values) => {
    const wasComplete = getOnboardingStatus(task) === 'complete';
    try {
      if (session?.token) {
        await updateLiveOnboardingTask(programme.key, task.id, values, true);
        setTasks((current) =>
          current.map((item) =>
            item.id === task.id
              ? { ...item, complete: true, status: 'complete', values }
              : item
          )
        );
      } else {
        setTasks(
          saveOnboardingTask(programme.key, task.id, values, true)
        );
      }
      setPortalMessage(
        wasComplete ? 'Changes saved.' : `“${task.title}” completed.`
      );
      closeDialog();
    } catch (requestError) {
      setPortalMessage(
        requestError.message || PROGRAMME_SYSTEM_MESSAGES.temporaryFailure
      );
    }
  };

  const goToView = (view) => {
    setActiveView(view);
    closeDialog();
  };

  const nextActionView = onboardingComplete
    ? programme.key === 'zone'
      ? 'credential'
      : 'cohort'
    : approved
      ? 'onboarding'
      : normalizedStatus === 'shortlisted'
        ? 'updates'
        : 'application';

  const renderFocusedView = () => {
    if (activeView === 'application') {
      return (
        <ApplicationSummary
          application={profile.isExample ? null : application}
          profile={profile}
          programme={programme}
        />
      );
    }
    if (activeView === 'credential') {
      return (
        <CredentialPanel
          credential={liveCredential}
          credentialState={credentialState}
          onOpen={() => openDialog('credential', null)}
          profile={profile}
        />
      );
    }
    if (activeView === 'updates') {
      return (
        <UpdatesPanel
                onOpen={(update) => openDialog('update', update)}
                programme={programme}
                updates={visibleUpdates}
        />
      );
    }
    if (activeView === 'resources') {
      return (
        <ResourcesPanel
          onOpen={(resource) => openDialog('resource', resource)}
          programme={programme}
          resources={portalData.resources}
        />
      );
    }
    if (activeView === 'cohort') {
      return (
        <CohortPanel
          members={portalData.cohort}
          onOpen={(member) => openDialog('member', member)}
        />
      );
    }
    if (activeView === 'onboarding') {
      return (
        <OnboardingPanel
          onOpen={(task) => openDialog('task', task)}
          tasks={tasks}
        />
      );
    }
    return null;
  };

  const focusedView = activeView !== 'overview';

  if (!session) {
    return <Navigate replace to={`/${programme.slug}/login`} />;
  }

  return (
    <>
      <SEO
        description={`${programme.title} participant portal.`}
        title={`${programme.portalName} | Supernova Lab`}
      />
      <main
        className={`pj-portal pj-theme--${programme.accent}`}
        data-programme={programme.key}
      >
        <PortalNav
          activeView={activeView}
          items={navItems}
          onChange={setActiveView}
          profile={profile}
          programme={programme}
        />

        <section className="pj-portal-main">
          <header className="pj-portal-header">
            <div>
              <h1>
                {programme.key === 'zone'
                  ? `Welcome back, ${profile.fullName.split(' ')[0]}.`
                  : `Welcome to your space, ${profile.fullName.split(' ')[0]}.`}
              </h1>
              <p>{programme.portalName}</p>
            </div>
            <div className="pj-portal-header__actions">
              <Link
                className="pj-mobile-signout"
                onClick={() => clearProgrammeSession(programme.key)}
                to={`/${programme.slug}/login`}
              >
                Sign out
              </Link>
            </div>
          </header>

          {portalMessage ? (
            <p
              aria-live="polite"
              className="pj-portal-live-message"
              role="status"
            >
              {portalMessage}
            </p>
          ) : null}

          <StatusStrip
            onboardingComplete={onboardingComplete}
            programme={programme}
            status={profile.applicationStatus}
          />

          {focusedView ? (
            <div className="pj-focused-view">
              <button
                className="pj-focused-view__back"
                onClick={() => setActiveView('overview')}
                type="button"
              >
                <ProgrammeIcon name="back" size={17} />
                Back to overview
              </button>
              {renderFocusedView()}
            </div>
          ) : programme.key === 'zone' ? (
            <div className="pj-zone-dashboard">
              {approved ? (
                <CredentialPanel
                  credential={liveCredential}
                  credentialState={credentialState}
                  onOpen={() => openDialog('credential', null)}
                  profile={profile}
                />
              ) : null}
              <PhaseTrack
                onboardingComplete={onboardingComplete}
                programme={programme}
                status={profile.applicationStatus}
              />
              <UpdatesPanel
                compact
                onOpen={(update) => openDialog('update', update)}
                onViewAll={() => setActiveView('updates')}
                programme={programme}
                updates={visibleUpdates}
              />
              <NextActionPanel
                approved={approved}
                onboardingComplete={onboardingComplete}
                onAction={() => setActiveView(nextActionView)}
                programme={programme}
                status={profile.applicationStatus}
              />
            </div>
          ) : (
            <div className="pj-fellow-dashboard">
              <PhaseTrack
                onboardingComplete={onboardingComplete}
                programme={programme}
                status={profile.applicationStatus}
              />
              <NextActionPanel
                approved={approved}
                onboardingComplete={onboardingComplete}
                onAction={() => setActiveView(nextActionView)}
                programme={programme}
                status={profile.applicationStatus}
              />
              <UpdatesPanel
                compact
                onOpen={(update) => openDialog('update', update)}
                onViewAll={() => setActiveView('updates')}
                programme={programme}
                updates={visibleUpdates}
              />
              {approved ? (
                <>
                  <OnboardingPanel
                    compact
                    onOpen={(task) => openDialog('task', task)}
                    tasks={tasks}
                  />
                  <CohortPanel
                    compact
                    members={portalData.cohort}
                    onOpen={(member) => openDialog('member', member)}
                    onViewAll={() => setActiveView('cohort')}
                  />
                  <ResourcesPanel
                    compact
                    onOpen={(resource) => openDialog('resource', resource)}
                    onViewAll={() => setActiveView('resources')}
                    programme={programme}
                    resources={portalData.resources}
                  />
                </>
              ) : null}
            </div>
          )}
        </section>

        {dialog?.type === 'update' ? (
          <PortalDialog
            eyebrow={dialog.item.date}
            onClose={closeDialog}
            title={dialog.item.title}
          >
            <div className="pj-dialog-copy">
              <p>{dialog.item.body}</p>
              <button
                className="pj-button pj-button--primary"
                onClick={() => goToView(dialog.item.actionView)}
                type="button"
              >
                {dialog.item.actionLabel}
                <ProgrammeIcon name="arrow" size={18} />
              </button>
            </div>
          </PortalDialog>
        ) : null}

        {dialog?.type === 'resource' ? (
          <PortalDialog
            eyebrow={dialog.item.type}
            onClose={closeDialog}
            title={dialog.item.title}
          >
            <div className="pj-dialog-copy">
              <p>{dialog.item.description}</p>
              <h3>Inside this resource</h3>
              <ul>
                {dialog.item.sections.map((section) => (
                  <li key={section}>
                    <ProgrammeIcon name="check" size={17} />
                    {section}
                  </li>
                ))}
              </ul>
              <p className="pj-dialog-note">
                This resource will open here once it is published by the
                programme team.
              </p>
            </div>
          </PortalDialog>
        ) : null}

        {dialog?.type === 'task' ? (
          <PortalDialog
            eyebrow={
              getOnboardingStatus(dialog.item) === 'complete'
                ? 'Completed task'
                : 'Onboarding'
            }
            onClose={closeDialog}
            title={dialog.item.title}
          >
            <TaskForm
              key={dialog.item.id}
              onSave={(values) => saveTask(dialog.item, values)}
              task={dialog.item}
            />
          </PortalDialog>
        ) : null}

        {dialog?.type === 'member' ? (
          <PortalDialog
            eyebrow={dialog.item.role}
            onClose={closeDialog}
            title={dialog.item.name}
          >
            <div className="pj-member-detail">
              <span>
                {dialog.item.profileImage ? (
                  <img alt="" src={dialog.item.profileImage} />
                ) : (
                  dialog.item.initials
                )}
              </span>
              <p>{dialog.item.focus}</p>
            </div>
          </PortalDialog>
        ) : null}

        {dialog?.type === 'credential' ? (
          <PortalDialog
            eyebrow="Active research credential"
            onClose={closeDialog}
            title={liveCredential?.credential_code || 'Research credential'}
          >
            <div className="pj-dialog-copy">
              <p>
                Issued to <strong>{profile.fullName}</strong> for participation
                in the Experimentation Zone. Valid until{' '}
                {formatCredentialDate(liveCredential?.expires_at)}.
              </p>
              <button
                className="pj-button pj-button--secondary"
                onClick={async () => {
                  await navigator.clipboard?.writeText(
                    liveCredential?.credential_code || ''
                  );
                  setCopied(true);
                }}
                type="button"
              >
                <ProgrammeIcon name="copy" size={18} />
                {copied ? 'Credential ID copied' : 'Copy credential ID'}
              </button>
            </div>
          </PortalDialog>
        ) : null}
      </main>
    </>
  );
};

export default ProgrammePortalPage;
